import apiClient from "./client";

/**
 * RailTel integration — frontend API wrappers.
 *
 * Rewritten for the provider's new API (their old one is gone). The
 * single biggest change: there's no "find by mobile number" endpoint
 * anymore, only exact-username lookup. So instead of searching, a
 * customer's RailTel username(s) are stored on our own side
 * (user_railtel_accounts) the moment the provider creates the account —
 * "lookup" now means "refresh what we already know", not "discover by
 * mobile". A customer can have at most 2 linked accounts.
 *
 * These all still call OUR OWN backend (portal_php), never the provider
 * directly — same reasons as before: the provider's Bearer token has to
 * stay server-side, and every other integration in this app already
 * works this way.
 *
 * GET  /customer/railtel_get_linked_accounts.php?customer_id=X
 *      Returns just what WE have on file — no live provider call. Used
 *      to decide how many accounts a customer has (0, 1, or 2) before
 *      doing anything else.
 *      -> { success: true, accounts: [{ id, railtel_username, created_at }] }
 *
 * POST /customer/railtel_link_account.php
 *      Body: { user_id, railtel_username }
 *      Verifies the username is real (calls the provider) before saving
 *      it, rejects if this customer already has 2 linked, and rejects if
 *      that username is already linked to a different customer.
 *      -> { success: true, id, provider_name, provider_mobile }
 *
 * POST /customer/railtel_unlink_account.php
 *      Body: { id }  (the linked-account row id, not the username)
 *      -> { success: true }
 *
 * GET  /customer/railtel_lookup_by_username.php?username=ap.phani87
 *      Live lookup against the provider for ONE known username — this
 *      is what actually gets the current package/expiry/balance/online
 *      status. Called once per linked account when the RailTel tab
 *      opens (max 2 calls), and again on manual refresh.
 *      -> { success: true, subscriber: { subscriberid, username, name,
 *           mobile, email, address, package, expiry, balance,
 *           online_status, is_active } }
 *
 * GET  /customer/railtel_subscriber_usage.php?username=ap.phani87
 *      Proxies the provider's /sessions endpoint. Already clean,
 *      structured JSON now — no more scraped-table parsing.
 *      -> { success: true, sessions: [{ "Start Time", "End Time",
 *           "Session Time", Upload, Download, Total, MAC, "Framed-IP",
 *           "Termination Cause" }], totals: {...} | null }
 *
 * GET  /customer/railtel_subscriber_online.php?username=ap.phani87
 *      -> { success: true, is_online: true, status: "Online" }
 */

export const railtelGetLinkedAccounts = (customerId) =>
  apiClient.get(`/customer/railtel_get_linked_accounts.php?customer_id=${customerId}`);

export const railtelLinkAccount = (userId, railtelUsername) =>
  apiClient.post("/customer/railtel_link_account.php", { user_id: userId, railtel_username: railtelUsername });

export const railtelUnlinkAccount = (accountId) =>
  apiClient.post("/customer/railtel_unlink_account.php", { id: accountId });

export const railtelLookupByUsername = (username) =>
  apiClient.get(`/customer/railtel_lookup_by_username.php?username=${encodeURIComponent(username)}`);

export const railtelGetSubscriberUsage = (username) =>
  apiClient.get(`/customer/railtel_subscriber_usage.php?username=${encodeURIComponent(username)}`);

export const railtelGetOnlineStatus = (username) =>
  apiClient.get(`/customer/railtel_subscriber_online.php?username=${encodeURIComponent(username)}`);

export const railtelRecharge = (username, customerId) =>
  apiClient.post("/customer/railtel_recharge.php", { username, customer_id: customerId });

export const getRailtelPlans = () => apiClient.get("/get_railtel_plans.php");

export const getRailtelPricedPlansForOperator = (customerId) =>
  apiClient.get(`/customer/get_railtel_plans.php?customer_id=${customerId}`);

export const getRailtelOperatorMapping = (operatorId) =>
  apiClient.get(`/railtel_operator_plan_mapping.php?operator_id=${operatorId}`);

export const saveRailtelOperatorMapping = (operatorId, mappings) =>
  apiClient.post("/railtel_operator_plan_mapping.php", { operator_id: operatorId, mappings });
